import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../hooks/useApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import {
  Phone, Mail, MapPin, MessageSquare, Clock, AlertCircle, ChevronsUpDown, Check, X,
  FileText, Briefcase, Search, ChevronLeft, ChevronRight, Loader2, Paperclip,
} from 'lucide-react';
import { activityFiles } from '../../lib/clientPage';
import { isVideoUrl } from '../../lib/videos';
import { VideoLink } from './VideoPlayer';

// ---------------------------------------------------------------------------
// Vocabulary (keys match backend/models.py)
// ---------------------------------------------------------------------------
export const ACTIVITY_TYPES = [
  { value: 'call', label: 'Called client', icon: Phone },
  { value: 'email', label: 'Sent email', icon: Mail },
  { value: 'visit', label: 'Visited client', icon: MapPin },
  { value: 'proposal_shown', label: 'Showed proposal', icon: FileText },
  { value: 'follow_up_invoice', label: 'Followed up on unpaid invoice', icon: AlertCircle, publishingOnly: true },
  { value: 'renewal_discussion', label: 'Discussed renewal', salesOnlyLabel: 'Discussed repeat work', icon: MessageSquare },
  { value: 'new_campaign', label: 'Discussed new campaign', salesOnlyLabel: 'Discussed new project', icon: Briefcase },
  { value: 'quote_requested', label: 'Client requested quote', icon: FileText },
  { value: 'not_interested', label: 'Client not interested', icon: X },
  { value: 'contact_later', label: 'Client asked to be contacted later', icon: Clock },
  { value: 'other', label: 'Other/custom note', icon: MessageSquare },
];

// The list a property offers when logging: a sales-team property has no
// invoices here and uses its own wording for two of the types.
export const activityTypesFor = (salesOnly) => ACTIVITY_TYPES
  .filter(a => !(salesOnly && a.publishingOnly))
  .map(a => (salesOnly && a.salesOnlyLabel ? { ...a, label: a.salesOnlyLabel } : a));

export const activityLabel = (value, salesOnly) => {
  const a = ACTIVITY_TYPES.find(t => t.value === value);
  if (!a) return value;
  return salesOnly && a.salesOnlyLabel ? a.salesOnlyLabel : a.label;
};

export const CONTACT_METHODS = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'in_person', label: 'In-person visit' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'Text/SMS' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'other', label: 'Other' },
];

// An activity's outcome is also the client's status (latest one wins).
export const OUTCOMES = [
  { value: 'new_lead', label: 'New lead', color: '#3B82F6' },
  { value: 'contacted', label: 'Contacted', color: '#60A5FA' },
  { value: 'follow_up_needed', label: 'Follow-up needed', color: '#F59E0B' },
  { value: 'proposal_sent', label: 'Proposal sent', color: '#A855F7' },
  { value: 'negotiating', label: 'Negotiating', color: '#FB923C' },
  { value: 'waiting_for_client', label: 'Waiting for client', color: '#94A3B8' },
  { value: 'won', label: 'Won', color: '#10B981' },
  { value: 'lost', label: 'Lost', color: '#EF4444' },
  { value: 'not_interested', label: 'Not interested', color: '#71717A' },
  { value: 'on_hold', label: 'On hold', color: '#A1A1AA' },
  { value: 'checkin', label: 'Existing client check-in', color: '#06B6D4' },
];

// Outcomes that close the conversation: no next follow-up date is required.
export const TERMINAL_OUTCOMES = ['won', 'lost', 'not_interested'];

export const OPP_STAGES = [
  { value: 'lead', label: 'Lead', color: '#3B82F6' },
  { value: 'contacted', label: 'Contacted', color: '#60A5FA' },
  { value: 'needs_analysis', label: 'Needs analysis', color: '#A855F7' },
  { value: 'proposal_sent', label: 'Proposal sent', color: '#F59E0B' },
  { value: 'negotiation', label: 'Negotiation', color: '#FB923C' },
  { value: 'won', label: 'Won', color: '#10B981' },
  { value: 'lost', label: 'Lost', color: '#EF4444' },
];

export const labelFor = (list, value) => list.find(x => x.value === value)?.label || value;
export const colorFor = (list, value) => list.find(x => x.value === value)?.color || '#94A3B8';

// ---------------------------------------------------------------------------
// Dates (always the viewer's LOCAL calendar day, never UTC)
// ---------------------------------------------------------------------------
const pad = (n) => String(n).padStart(2, '0');
export const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayISO = () => isoDate(new Date());
export const addDaysISO = (days, from) => {
  const d = from ? new Date(`${from}T12:00:00`) : new Date();
  d.setDate(d.getDate() + days);
  return isoDate(d);
};
// "2026-10-02" -> local date string, without the off-by-one a bare Date() gives.
export const showDay = (iso) => {
  if (!iso) return '';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

// Files are served by the backend, so a stored path needs its address in front.
export const fileHref = (url) => (url && url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

// Links to the files attached to an activity.
export const AttachmentLinks = ({ activity, className = '' }) => {
  const files = activityFiles(activity);
  if (!files.length) return null;
  return (
    <div className={`flex flex-col gap-0.5 items-start max-w-full ${className}`}>
      {files.map((f, i) => (isVideoUrl(f.url) ? (
        <VideoLink
          key={f.url}
          file={f}
          className="inline-flex items-center gap-1 text-xs text-[#3B82F6] hover:underline max-w-full"
          iconClassName="w-3 h-3 shrink-0"
          testId={`attachment-video-${activity.id}-${i}`}
        />
      ) : (
        <a
          key={f.url}
          href={fileHref(f.url)} target="_blank" rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-xs text-[#3B82F6] hover:underline max-w-full"
          data-testid={`attachment-link-${activity.id}-${i}`}
        >
          <Paperclip className="w-3 h-3 shrink-0" />
          <span className="truncate">{f.name || 'File'}</span>
        </a>
      )))}
    </div>
  );
};

export const apiError = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(' ');
  return e?.message || fallback;
};

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------
export const StatusPill = ({ status }) => {
  const value = status || 'new_lead';
  const color = colorFor(OUTCOMES, value);
  return (
    <span
      className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide whitespace-nowrap"
      style={{ backgroundColor: `${color}22`, color }}
      data-testid={`status-pill-${value}`}
    >
      {labelFor(OUTCOMES, value)}
    </span>
  );
};

export const EmptyMsg = ({ text }) => (
  <div className="py-8 text-center text-sm text-[#94A3B8]">{text}</div>
);

export const Pager = ({ page, pages, total, pageSize, onPage, testid = 'pager' }) => {
  if (!total) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between gap-3 text-sm text-[#94A3B8]" data-testid={testid}>
      <span data-testid={`${testid}-range`}>{from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}
          className="border-[#1E293B] bg-[#121212] text-[#94A3B8] h-8" data-testid={`${testid}-prev`}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="font-mono text-xs">Page {page} of {pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)}
          className="border-[#1E293B] bg-[#121212] text-[#94A3B8] h-8" data-testid={`${testid}-next`}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Server-side client search (filtering, sorting and paging happen in the API)
// ---------------------------------------------------------------------------
export const buildClientQuery = (filters, extra = {}) => {
  const params = new URLSearchParams();
  Object.entries({ ...filters, ...extra }).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '' || v === 'all' || v === false) return;
    params.append(k, v === true ? '1' : v);
  });
  return params.toString();
};

export const useClientSearch = (filters, { pageSize = 50, sort = 'name', enabled = true } = {}) => {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [debounced, setDebounced] = useState(filters);
  const seq = useRef(0);
  const key = JSON.stringify(filters);

  // Wait until typing pauses, and go back to page 1 whenever a filter changes.
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(JSON.parse(key)); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [key]);

  const debouncedKey = JSON.stringify(debounced);
  const load = useCallback(async () => {
    if (!enabled) return;
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const qs = buildClientQuery(JSON.parse(debouncedKey), { page, page_size: pageSize, sort });
      const res = await api.get(`/advertisers/search?${qs}`);
      if (mine === seq.current) setData(res);     // ignore answers that arrive out of order
    } catch (e) {
      if (mine === seq.current) setError(apiError(e, 'Could not load clients'));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [debouncedKey, page, pageSize, sort, enabled]);

  useEffect(() => { load(); }, [load]);

  return { ...data, page, setPage, pageSize, loading, error, reload: load, appliedFilters: debounced };
};

// ---------------------------------------------------------------------------
// Filter bar shared by the Clients page, the Call list and Needs details
// ---------------------------------------------------------------------------
export const ClientFilters = ({
  filters, onChange, facets, salesReps = [], isSalesRep = false, everyRep = false,
  show = { industry: true, rep: true, status: true, hasPhone: true, postal: true },
  placeholder = 'Search name, contact, phone, email, address, docket…',
}) => {
  const set = (patch) => onChange({ ...filters, ...patch });
  const selectCls = 'bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-sm';
  return (
    <div className="flex flex-wrap items-center gap-2 bg-[#121212] border border-[#1E293B] rounded-lg p-3" data-testid="client-filters">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <Input
          value={filters.q || ''}
          onChange={(e) => set({ q: e.target.value })}
          placeholder={placeholder}
          className="pl-9 bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9"
          data-testid="client-search-input"
        />
      </div>
      {show.rep && (
        <Select value={filters.rep || 'all'} onValueChange={(v) => set({ rep: v })}>
          <SelectTrigger className={`${selectCls} w-[170px]`} data-testid="client-rep-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-[#1E293B]">
            {isSalesRep ? (
              <>
                <SelectItem value="mine" className="text-[#F8FAFC]">My clients</SelectItem>
                <SelectItem value="unassigned" className="text-[#F8FAFC]">Available (unassigned)</SelectItem>
                {/* The client list shows everyone's clients; Sales work lists stay on your own. */}
                {everyRep && <SelectItem value="all" className="text-[#F8FAFC]">All clients</SelectItem>}
                {everyRep && salesReps.map(r => <SelectItem key={r.id} value={r.id} className="text-[#F8FAFC]">{r.name}</SelectItem>)}
              </>
            ) : (
              <>
                <SelectItem value="all" className="text-[#F8FAFC]">All reps</SelectItem>
                <SelectItem value="unassigned" className="text-[#F8FAFC]">Unassigned</SelectItem>
                {salesReps.map(r => <SelectItem key={r.id} value={r.id} className="text-[#F8FAFC]">{r.name}</SelectItem>)}
              </>
            )}
          </SelectContent>
        </Select>
      )}
      {show.industry && (
        <Select value={filters.industry || 'all'} onValueChange={(v) => set({ industry: v })}>
          <SelectTrigger className={`${selectCls} w-[200px]`} data-testid="client-industry-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-[#1E293B] max-h-[320px]">
            <SelectItem value="all" className="text-[#F8FAFC]">All industries</SelectItem>
            {(facets?.industries || []).map(name => (
              <SelectItem key={name} value={name} className="text-[#F8FAFC]">
                {name} ({(facets.by_industry?.[name] || 0).toLocaleString()})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {show.status && (
        <Select value={filters.status || 'all'} onValueChange={(v) => set({ status: v })}>
          <SelectTrigger className={`${selectCls} w-[170px]`} data-testid="client-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-[#1E293B]">
            <SelectItem value="all" className="text-[#F8FAFC]">Any status</SelectItem>
            {OUTCOMES.map(o => <SelectItem key={o.value} value={o.value} className="text-[#F8FAFC]">{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {show.postal && (
        <Input
          value={filters.postal || ''}
          onChange={(e) => set({ postal: e.target.value })}
          placeholder="Postal code starts with…"
          className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 w-[180px]"
          data-testid="client-postal-filter"
        />
      )}
      {show.hasPhone && (
        <label className="flex items-center gap-2 text-sm text-[#94A3B8] cursor-pointer select-none h-9">
          <input
            type="checkbox"
            checked={!!filters.has_phone}
            onChange={(e) => set({ has_phone: e.target.checked })}
            className="accent-[#3B82F6]"
            data-testid="client-has-phone-filter"
          />
          Has a phone
        </label>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Client picker that searches the server as you type (works with any number
// of clients; never loads the whole list)
// ---------------------------------------------------------------------------
export const ClientPicker = ({ value, valueLabel, onChange, disabled, testid = 'client-picker' }) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const mine = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/advertisers/search?${buildClientQuery({ q: term }, { page_size: 20 })}`);
        if (mine === seq.current) setResults(res.items || []);
      } catch (_) {
        if (mine === seq.current) setResults([]);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [term, open]);

  return (
    <Popover open={open} onOpenChange={(o) => { if (!disabled) setOpen(o); }}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" role="combobox" disabled={disabled}
          className="w-full justify-between bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] hover:bg-[#1A1A1A] font-normal"
          data-testid={testid}
        >
          <span className="truncate">{value ? (valueLabel || 'Selected client') : 'Pick a client…'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[460px] max-w-[90vw] p-0 bg-[#121212] border-[#1E293B]" align="start">
        <div className="flex items-center border-b border-[#1E293B] px-3">
          <Search className="w-4 h-4 text-[#94A3B8] mr-2 shrink-0" />
          <input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Type a name, phone or email…"
            className="flex-1 h-10 bg-transparent text-sm text-[#F8FAFC] outline-none placeholder:text-[#475569]"
            data-testid={`${testid}-input`}
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" />}
        </div>
        <div className="max-h-[280px] overflow-y-auto py-1" data-testid={`${testid}-results`}>
          {!loading && results.length === 0 && (
            <div className="py-4 text-center text-sm text-[#94A3B8]">No client found.</div>
          )}
          {results.map(c => (
            <button
              type="button" key={c.id}
              onClick={() => { onChange(c); setOpen(false); setTerm(''); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-[#F8FAFC] hover:bg-[#1E293B]"
              data-testid={`${testid}-option-${c.id}`}
            >
              <Check className={`h-4 w-4 shrink-0 ${value === c.id ? 'opacity-100 text-[#3B82F6]' : 'opacity-0'}`} />
              <span className="flex-1 min-w-0">
                <span className="block truncate">{c.business_name}</span>
                {(c.address || c.phone) && (
                  <span className="block truncate text-xs text-[#475569]">{[c.phone, c.address].filter(Boolean).join(' · ')}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
